import React, { useState, useEffect, useCallback, useRef } from 'react';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';

// Define types and interfaces
interface Language {
  code: string;
  name: string;
}

interface SpeechToTextProps {
  onTranscriptChange: (transcript: string) => void;
}

// Type for a speech recognition result
interface SpeechRecognitionResult {
  isFinal: boolean;
  [index: number]: {
    transcript: string;
  };
}

// Type for the speech recognition instance
interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  onresult: (event: SpeechRecognitionEvent) => void;
  onerror: (event: SpeechRecognitionErrorEvent) => void;
  onend: () => void;
}

// Type for recognition error event
interface SpeechRecognitionErrorEvent {
  error: string;
}

// Type for recognition result event
interface SpeechRecognitionEvent {
  resultIndex: number;
  results: {
    [index: number]: SpeechRecognitionResult;
    length: number;
  };
}

// Define the window with speech recognition
interface WindowWithSpeechRecognition extends Window {
  SpeechRecognition?: new () => SpeechRecognitionInstance;
  webkitSpeechRecognition?: new () => SpeechRecognitionInstance;
}

const SpeechToText: React.FC<SpeechToTextProps> = ({ onTranscriptChange }) => {
  const [isListening, setIsListening] = useState<boolean>(false);
  const [transcript, setTranscript] = useState<string>('');
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en-IN'); // Default: English (India)
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  
  // Add state to track if the recognition engine is ready.
  const [isReady, setIsReady] = useState<boolean>(false);

  // Use useRef to keep the recognition instance and active state stable across renders
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  // Track if we are actually intending to listen, regardless of recognition state
  const activeListeningRef = useRef<boolean>(false);
  // Track if we're currently trying to restart recognition
  const isRestartingRef = useRef<boolean>(false);

  // Expanded list of Indic languages + English - memoized to prevent recreating on each render
  const languages: Language[] = React.useMemo(() => [
    { code: 'en-IN', name: 'English (India)' },
    { code: 'hi-IN', name: 'Hindi (India)' },
    { code: 'pa-IN', name: 'Punjabi (India)' },
    { code: 'te-IN', name: 'Telugu (India)' },
    { code: 'ta-IN', name: 'Tamil (India)' },
    { code: 'kn-IN', name: 'Kannada (India)' },
    { code: 'ml-IN', name: 'Malayalam (India)' },
    { code: 'bn-IN', name: 'Bengali (India)' },
    { code: 'gu-IN', name: 'Gujarati (India)' },
    { code: 'mr-IN', name: 'Marathi (India)' },
  ], []);

  // Function to safely start recognition
  const startRecognition = useCallback(() => {
    if (!recognitionRef.current || isRestartingRef.current) return;
    
    try {
      recognitionRef.current.start();
      setIsListening(true);
    } catch (e) {
      console.error('Failed to start speech recognition:', e);
    }
  }, []);

  // Initialize speech recognition
  useEffect(() => {
    const windowWithSpeech = window as WindowWithSpeechRecognition;
    
    if (windowWithSpeech.SpeechRecognition || windowWithSpeech.webkitSpeechRecognition) {
      const SpeechRecognition = windowWithSpeech.SpeechRecognition || windowWithSpeech.webkitSpeechRecognition;
      
      if (SpeechRecognition) {
        const recognitionInstance = new SpeechRecognition();
        
        recognitionInstance.continuous = true;
        recognitionInstance.interimResults = true;
        recognitionInstance.lang = selectedLanguage;

        recognitionInstance.onresult = (event: SpeechRecognitionEvent) => {
          for (let i = event.resultIndex; i < event.results.length; i++) {
            if (event.results[i].isFinal) {
                onTranscriptChange(event.results[i][0].transcript);
            }
          }
        };

        recognitionInstance.onerror = (event: SpeechRecognitionErrorEvent) => {
          console.error('Speech recognition error:', event.error);
          // Only stop completely on critical errors
          if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
            activeListeningRef.current = false;
            setIsListening(false);
            setOpenDialog(false);
          }
        };

        recognitionInstance.onend = () => {
          // Automatically restart if we're supposed to be actively listening
          if (activeListeningRef.current) {
            // Prevent rapid restart attempts by adding a small delay
            isRestartingRef.current = true;
            setTimeout(() => {
              if (activeListeningRef.current) {
                try {
                  recognitionInstance.start();
                  setIsListening(true); // Explicitly set this to ensure UI matches state
                } catch (e) {
                  console.error('Failed to restart speech recognition:', e);
                  activeListeningRef.current = false;
                  setIsListening(false);
                }
              }
              isRestartingRef.current = false;
            }, 300);
          } else {
            setIsListening(false);
          }
        };

        recognitionRef.current = recognitionInstance;
        // Update state to trigger a re-render and enable the button.
        setIsReady(true);
      }
    } else {
      console.error('Speech recognition not supported');
      // Ensure the button remains disabled if not supported.
      setIsReady(false);
    }
    
    // Cleanup function
    return () => {
      activeListeningRef.current = false;
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          console.error('Error stopping recognition:', e);
        }
      }
    };
  }, [selectedLanguage, onTranscriptChange, startRecognition]);

  // Handle language change - memoized with useCallback
  const handleLanguageChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedLanguage(event.target.value);
    const wasListening = activeListeningRef.current;
    
    // Stop current recognition
    if (recognitionRef.current) {
      activeListeningRef.current = false;
      try {
        recognitionRef.current.stop();
      } catch (e) {
        console.error('Error stopping recognition:', e);
      }
      setIsListening(false);
    }
    
    // Remember if we need to restart with new language
    // This will be handled when the recognitionRef is updated with new language
    activeListeningRef.current = wasListening;
  }, []);

  // Toggle listening and dialog - memoized with useCallback
  const toggleListening = useCallback(() => {
    if (!isReady) return;

    if (isListening) {
      // Stop listening
      activeListeningRef.current = false;
      try {
        recognitionRef.current?.stop();
      } catch (e) {
        console.error('Error stopping recognition:', e);
      }
      setTranscript('');
      setIsListening(false);
    } else {
      // Open dialog to start listening
      setOpenDialog(true);
    }
  }, [isListening, isReady]);

  // Start listening from dialog - memoized with useCallback
  const startListening = useCallback(() => {
    if (recognitionRef.current) {
      activeListeningRef.current = true;
      startRecognition();
      setOpenDialog(false);
    }
  }, [startRecognition]);

  // Close dialog - memoized with useCallback
  const closeDialog = useCallback(() => {
    setOpenDialog(false);
  }, []);

  // Effect to sync activeListeningRef with isListening
  useEffect(() => {
    // If listening state changes, check if we need to restart
    if (isListening && !activeListeningRef.current) {
      activeListeningRef.current = true;
    }
    
    // If we're supposed to be listening but recognition isn't active, restart it
    if (activeListeningRef.current && !isListening && !isRestartingRef.current) {
      startRecognition();
    }
  }, [isListening, startRecognition]);
  
  return (
    <>
      <button
        onClick={toggleListening}
        className="p-2 text-gray-600 hover:text-gray-800"
        disabled={!isReady}
        type="button"
      >
        {isListening ? <StopIcon /> : <MicIcon />}
      </button>

      {/* Dialog for Language Selection and Start */}
      {openDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Select Language</h2>
            <div className="flex flex-col gap-4">
              {/* ✅ FIX: Added specific dark mode classes to ensure visibility */}
              <select
                value={selectedLanguage}
                onChange={handleLanguageChange}
                className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-black dark:bg-gray-700 dark:text-white dark:border-gray-600"
              >
                {languages.map((lang) => (
                  <option
                    key={lang.code}
                    value={lang.code}
                    className="text-black dark:text-white dark:bg-gray-700"
                  >
                    {lang.name}
                  </option>
                ))}
              </select>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={closeDialog}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 dark:border-gray-500 dark:text-gray-200 dark:hover:bg-gray-600"
                  type="button"
                >
                  Cancel
                </button>
                <button
                  onClick={startListening}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-black"
                  type="button"
                >
                  Start Listening
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SpeechToText;